import React, { useState, useEffect } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import {
  REVIEW_ACTION_LABELS,
  type AuditReviewState,
  type ReviewAction,
} from '@tinypro-audit/contracts';
import { formatDateTimeInAppTimeZone } from '@tinypro-audit/shared/time';
import { useRevalidator } from 'react-router-dom';
import { api } from '../api/client';
import { ReviewDecisionEditor } from './ReviewDecisionEditor';

const questions = [
  {
    key: 'caughtJobsiteAlertIssue',
    legacy: 'caught_jobsite_alert_issue',
    label:
      'Did the audit catch an issue that could have caused a jobsite alert?',
  },
  {
    key: 'awareOfPotentialInstallationIssue',
    legacy: 'aware_of_potential_installation_issue',
    label:
      'Did this audit report help you become aware of a potential installation issue?',
  },
  {
    key: 'tookCorrectiveAction',
    legacy: 'took_corrective_action',
    label:
      'Did you take corrective actions on the job to make sure we do not have a jobsite alert?',
  },
] as const;

export function AuditReviewStateCard({
  auditId,
  initialReviewState,
}: {
  auditId: string;
  initialReviewState: AuditReviewState;
}) {
  const [state, setState] = useState(initialReviewState);
  const [answers, setAnswers] = useState(initialReviewState.answers);
  const [explanations, setExplanations] = useState(
    initialReviewState.explanations,
  );
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [saved, setSaved] = useState(false);
  const revalidator = useRevalidator();
  useEffect(() => {
    setState(initialReviewState);
    setAnswers(initialReviewState.answers);
    setExplanations(initialReviewState.explanations);
  }, [initialReviewState]);
  const save = async (action: ReviewAction | 'feedback', note?: string) => {
    const next = await api.upsertAuditReviewState(auditId, {
      action,
      sourceVersion: state.sourceVersion!,
      expectedUpdatedAt: state.updatedAt,
      expectedRevision: state.revision,
      ...(action === 'feedback' ? { answers, explanations } : { note }),
    });
    setState(next);
    revalidator.revalidate();
  };
  return (
    <Card withBorder radius="md">
      <Stack gap="lg">
        <ReviewDecisionEditor
          decision={state.decision}
          revision={`${state.sourceVersion}:${state.revision}`}
          scope="Applies to this pre-site assessment. The original findings and grade are preserved."
          disabledReason={
            !state.sourceVersion
              ? 'Reload to identify the report being reviewed.'
              : undefined
          }
          correctionHelp="Confirm that you fixed and checked this finding. Saving closes the portal follow-up as Resolved by reviewer. The original pre-site findings and grade stay unchanged."
          onSave={(action, note) => save(action, note)}
        />
        <Accordion multiple>
          <Accordion.Item value="feedback">
            <Accordion.Control>
              Review feedback{' '}
              <Text component="span" size="sm" c="dimmed">
                · optional
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <Text size="sm" c="dimmed">
                  Feedback measures how useful the report was. It does not close
                  a follow-up or change verification.
                </Text>
                {questions.map((q) => (
                  <Stack key={q.key} gap="xs">
                    <Text size="sm" fw={500}>
                      {q.label}
                    </Text>
                    <SegmentedControl
                      aria-label={q.label}
                      value={
                        answers[q.key] === null
                          ? 'unanswered'
                          : answers[q.key]
                            ? 'yes'
                            : 'no'
                      }
                      data={[
                        { value: 'unanswered', label: 'Unanswered' },
                        { value: 'yes', label: 'Yes' },
                        { value: 'no', label: 'No' },
                      ]}
                      onChange={(v) => {
                        setAnswers((a) => ({
                          ...a,
                          [q.key]: v === 'unanswered' ? null : v === 'yes',
                        }));
                        setSaved(false);
                      }}
                    />
                    <Textarea
                      label="Context (optional)"
                      autosize
                      minRows={1}
                      maxLength={4000}
                      value={explanations[q.key] ?? ''}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        setExplanations((x) => ({
                          ...x,
                          [q.key]: value,
                        }));
                        setSaved(false);
                      }}
                    />
                  </Stack>
                ))}
                {error && <Alert color="red">{error}</Alert>}
                <Group>
                  <Button
                    variant="default"
                    loading={busy}
                    disabled={!state.sourceVersion}
                    onClick={async () => {
                      setBusy(true);
                      setError('');
                      try {
                        await save('feedback');
                        setSaved(true);
                      } catch (e) {
                        setError(
                          e instanceof Error
                            ? e.message
                            : 'Could not save feedback.',
                        );
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Save feedback
                  </Button>
                  {saved && (
                    <Text size="sm" c="teal">
                      Feedback saved.
                    </Text>
                  )}
                </Group>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
          {!!state.history.length && (
            <Accordion.Item value="history">
              <Accordion.Control>
                Review history · {state.history.length}{' '}
                {state.history.length === 1 ? 'record' : 'records'}
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  {state.history.map((event) => (
                    <div key={event.id}>
                      <Group gap="xs">
                        <Text size="sm" fw={600}>
                          {event.action === 'feedback'
                            ? 'Feedback saved'
                            : event.action === 'legacy_review'
                              ? 'Earlier review record'
                              : (REVIEW_ACTION_LABELS[
                                  event.action as ReviewAction
                                ] ?? event.action)}
                        </Text>
                        {event.source_version !== state.sourceVersion && (
                          <Badge size="xs" variant="light" color="gray">
                            {event.source_version
                              ? 'Earlier report'
                              : 'Unversioned record'}
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {event.actor_name ?? 'Reviewer'} ·{' '}
                        {formatDateTimeInAppTimeZone(event.created_at)}
                      </Text>
                      {typeof event.details.note === 'string' && (
                        <Text size="sm">{event.details.note}</Text>
                      )}
                      {(event.action === 'feedback' ||
                        event.action === 'legacy_review') && (
                        <Stack gap={4} mt="xs">
                          {questions.map((q) => {
                            const old = event.details;
                            const a =
                              (
                                old.answers as
                                  | Record<string, unknown>
                                  | undefined
                              )?.[q.key] ?? old[q.legacy];
                            const explanation =
                              (
                                old.explanations as
                                  | Record<string, unknown>
                                  | undefined
                              )?.[q.key] ?? old[`${q.legacy}_explanation`];
                            return (
                              <div key={q.key}>
                                <Text size="xs">
                                  {q.label}{' '}
                                  <strong>
                                    {a === true
                                      ? 'Yes'
                                      : a === false
                                        ? 'No'
                                        : 'Unanswered'}
                                  </strong>
                                </Text>
                                {typeof explanation === 'string' && (
                                  <Text size="xs" c="dimmed">
                                    {explanation}
                                  </Text>
                                )}
                              </div>
                            );
                          })}
                        </Stack>
                      )}
                      {event.action === 'legacy_review' && (
                        <Text size="xs" c="dimmed">
                          Previous status:{' '}
                          {String(event.details.status ?? 'Unmarked')}. This
                          record was not tied to a specific report version.
                        </Text>
                      )}
                    </div>
                  ))}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          )}
        </Accordion>
      </Stack>
    </Card>
  );
}
